"use client";

import { useEffect, useMemo, useRef } from "react";
import * as LightweightCharts from "lightweight-charts";
import "./ChartFauxMarche.css";

const Y_MIN = 60;
const Y_MAX = 130;


const VISIBLE_BARS = 16;
const PAD_BARS = 6;

const CONFIG = {
  max: 130,
  base: 80,
  randMin: 1,
  randMax: 20,
  crashBoost: -30,

  historyLength: 120,

  rightOffset: 22,
  barSpacing: 50,
  priceScaleTop: 0.15,
  priceScaleBottom: 0.1,

  barMs: 2000,

  crashWindows: [
    ["30/09/2025 19:50:30", "30/09/2025 19:52:30"],
    ["30/09/2025 21:50:30", "30/09/2025 21:52:30"],
  ],
};


function parseFrDateTime(str) {
  const [d, t = "00:00:00"] = str.trim().split(" ");
  const [DD, MM, YYYY] = d.split("/").map(Number);
  const [hh, mm, ss] = t.split(":").map(Number);
  return new Date(YYYY, MM - 1, DD, hh, mm, ss || 0);
}

function isInInterval(date, start, end) {
  const t = date.getTime();
  return t >= start.getTime() && t <= end.getTime();
}


function hashFloat(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  h ^= h << 13; h >>>= 0;
  h ^= h >> 17; h >>>= 0;
  h ^= h << 5;  h >>>= 0;
  return (h >>> 0) / 4294967296;
}

function timeFloat(date, salt = "") {
  const yyyy = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mn = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return hashFloat(`${yyyy}${MM}${dd}${hh}${mn}${ss}:${salt}`);
}

function timeInt(min, max, date, salt = "") {
  const f = timeFloat(date, salt);
  return Math.floor(f * (max - min + 1)) + min;
}

function computeCrashingNow(date, crashWindows) {
  for (const [startS, endS] of crashWindows) {
    const start = parseFrDateTime(startS);
    const end = parseFrDateTime(endS);
    if (isInInterval(date, start, end)) return true;
  }
  return false;
}

const slotIndexFromNow = (barMs) => Math.floor(Date.now() / barMs);

const slotMs = (slotIndex, barMs) => slotIndex * barMs;

export default function ChartFauxMarche({ config, candlestickStyle }) {
  const cfg = useMemo(() => ({ ...CONFIG, ...(config || {}) }), [config]);

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const dataRef = useRef([]);
  const lastSlotRef = useRef(null);
  const rafRef = useRef(null);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const applyWindow = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const total = dataRef.current.length;
    const from = Math.max(0, total - (VISIBLE_BARS + PAD_BARS));
    const to = total + PAD_BARS;
    chart.timeScale().setVisibleLogicalRange({ from, to });
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = LightweightCharts.createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { type: "Solid", color: "#0b0e14" }, textColor: "#c8d1e6" },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        borderVisible: false,
        rightOffset: cfg.rightOffset,
        barSpacing: cfg.barSpacing,
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: cfg.priceScaleTop, bottom: cfg.priceScaleBottom },
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.06)" },
        horzLines: { color: "rgba(255,255,255,0.06)" },
      },
      localization: { locale: "fr-FR" },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;

    const series = chart.addCandlestickSeries({
      upColor: "#8ceb93",
      downColor: "#de6969ff",
      borderUpColor: "#8ceb92ff",
      borderDownColor: "#de6969ff",
      wickUpColor: "#8ceb93",
      wickDownColor: "#de6969ff",
      priceLineVisible: true,
      priceLineColor: "#de6969ff",
      priceLineWidth: 1,
      priceLineStyle: LightweightCharts.LineStyle.Dashed,
      priceFormat: { type: "price", precision: 0, minMove: 1 },
      ...(candlestickStyle || {}),
    });
    seriesRef.current = series;

    series.applyOptions({
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: Y_MIN, maxValue: Y_MAX },
      }),
    });

    const barMs = Math.max(1000, cfg.barMs || 2000);

    const buildBarForSlot = (slotIdx, prevClose) => {
      const tMs = slotMs(slotIdx, barMs);
      const d = new Date(tMs);

      const inCrash = computeCrashingNow(d, cfg.crashWindows);
      const r = timeInt(cfg.randMin, cfg.randMax, d, "r");
      const crashAdd = inCrash ? cfg.crashBoost : 0;

      const open = prevClose ?? cfg.base;
      const target = clamp(cfg.base + r + crashAdd, 1, cfg.max);

      const jitter = (timeFloat(d, "closeJit") - 0.5) * 2;
      const highJ  = timeFloat(d, "highJit") * 2.5;
      const lowJ   = timeFloat(d, "lowJit") * 2.5;

      const close = clamp((open + target) / 2 + jitter, 1, cfg.max);
      const high  = clamp(Math.max(open, close) + highJ, 1, cfg.max);
      const low   = clamp(Math.min(open, close) - lowJ, 1, cfg.max);

      return { time: Math.floor(tMs / 1000), open, high, low, close };
    };

    const initHistory = (currentSlot) => {
      dataRef.current = [];
      let prevClose = cfg.base;
      const startSlot = currentSlot - cfg.historyLength + 1;
      for (let s = startSlot; s <= currentSlot; s++) {
        const bar = buildBarForSlot(s, prevClose);
        dataRef.current.push(bar);
        prevClose = bar.close;
      }
      series.setData(dataRef.current);
      applyWindow();
      lastSlotRef.current = currentSlot;
    };
    const catchUpTo = (toSlot) => {
      if (lastSlotRef.current === null || dataRef.current.length === 0) {
        initHistory(toSlot);
        return;
      }
      let prevClose = dataRef.current[dataRef.current.length - 1]?.close ?? cfg.base;
      for (let s = lastSlotRef.current + 1; s <= toSlot; s++) {
        const bar = buildBarForSlot(s, prevClose);
        dataRef.current.push(bar);
        if (dataRef.current.length > cfg.historyLength) {
          dataRef.current = dataRef.current.slice(-cfg.historyLength);
        }
        prevClose = bar.close;
      }
      series.setData(dataRef.current);
      applyWindow();
      lastSlotRef.current = toSlot;
    };

    initHistory(slotIndexFromNow(barMs));

    const loop = () => {
      const currentSlot = slotIndexFromNow(barMs);

      if (lastSlotRef.current !== null && currentSlot < lastSlotRef.current) {
        initHistory(currentSlot);
      }
      else if (lastSlotRef.current === null || currentSlot > lastSlotRef.current) {
        catchUpTo(currentSlot);
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => applyWindow());
    ro.observe(containerRef.current);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      chart.remove();
    };
  }, [cfg, candlestickStyle]);

  return <div ref={containerRef} className="chart-container" />;
}
